import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, FileDiff } from "lucide-react";

export default function ResumeDiffPanel({ candidateId }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const { data } = await api.get(`/candidates/${candidateId}/resume-diff`);
                setData(data);
            } catch (err) { toast.error(formatApiError(err)); }
            finally { setLoading(false); }
        })();
    }, [candidateId]);

    if (loading) return <div className="text-sm font-mono text-foreground/80">Loading resume diff…</div>;
    if (!data) return null;
    if (!data.has_previous) {
        return (
            <div className="border border-dashed border-border p-8 text-center" data-testid="resume-diff-empty">
                <FileDiff className="w-8 h-8 mx-auto text-muted-foreground" />
                <h3 className="font-display font-bold text-lg mt-3">No previous version</h3>
                <p className="text-sm text-foreground/80 mt-1">Resume diff appears after the candidate's resume is replaced at least once.</p>
            </div>
        );
    }

    const archivedAt = data.previous_archived_at ? new Date(data.previous_archived_at).toLocaleString() : "—";

    return (
        <div className="space-y-6" data-testid="resume-diff-panel">
            <div className="border border-border bg-white p-4">
                <div className="text-[10px] uppercase tracking-widest font-mono text-ikb">Comparing against previous version</div>
                <div className="font-mono text-sm mt-1">
                    <strong>{data.previous_filename || "—"}</strong> · archived {archivedAt}
                </div>
            </div>

            <DiffSection
                title="Skills"
                added={data.skills?.added || []}
                removed={data.skills?.removed || []}
                unchangedCount={data.skills?.unchanged_count || 0}
                renderItem={(s) => <span className="font-mono text-[11px] uppercase tracking-widest">{s}</span>}
            />

            <DiffSection
                title="Experience entries"
                added={data.experience?.added || []}
                removed={data.experience?.removed || []}
                unchangedCount={data.experience?.unchanged_count || 0}
                renderItem={(e) => (
                    <div className="min-w-0">
                        <div className="font-medium text-sm truncate">{e.title || "—"}</div>
                        <div className="text-xs text-foreground/70 truncate">{e.company || "—"}{e.start || e.end ? ` · ${e.start || ""}${e.end ? " → " + e.end : ""}` : ""}</div>
                    </div>
                )}
            />

            <DiffSection
                title="Education"
                added={data.education?.added || []}
                removed={data.education?.removed || []}
                renderItem={(s) => <span className="text-sm">{s || "—"}</span>}
            />
        </div>
    );
}

function DiffSection({ title, added = [], removed = [], unchangedCount, renderItem }) {
    return (
        <section data-testid={`diff-section-${title.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="flex items-end justify-between mb-2 gap-3 flex-wrap">
                <h3 className="font-display font-bold text-base">{title}</h3>
                {typeof unchangedCount === "number" && (
                    <span className="text-[10px] font-mono uppercase tracking-widest text-foreground/70">
                        {unchangedCount} unchanged · {added.length} added · {removed.length} removed
                    </span>
                )}
            </div>
            <div className="grid md:grid-cols-2 gap-px bg-border border border-border">
                <div className="bg-emerald-50/40 p-4 min-h-[60px]">
                    <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-emerald-700 mb-2">
                        <ArrowUp className="w-3 h-3" /> Added ({added.length})
                    </div>
                    {added.length === 0 ? (
                        <div className="text-xs text-foreground/60 italic">No additions</div>
                    ) : (
                        <ul className="space-y-1.5">
                            {added.map((item, i) => (
                                <li key={i} className="border-l-2 border-emerald-400 pl-2 py-0.5">{renderItem(item)}</li>
                            ))}
                        </ul>
                    )}
                </div>
                <div className="bg-rose-50/40 p-4 min-h-[60px]">
                    <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-rose-700 mb-2">
                        <ArrowDown className="w-3 h-3" /> Removed ({removed.length})
                    </div>
                    {removed.length === 0 ? (
                        <div className="text-xs text-foreground/60 italic">No removals</div>
                    ) : (
                        <ul className="space-y-1.5">
                            {removed.map((item, i) => (
                                <li key={i} className="border-l-2 border-rose-400 pl-2 py-0.5">{renderItem(item)}</li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </section>
    );
}
